import React from "react";
import dayjs from "dayjs";
import {
    Area,
    AreaChart,
    Brush,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { isMobileViewport } from "../../utils/responsive";
import { getPaddedDomain } from "../../utils/chartDomain";
import { showToast } from "../../utils/show_toast";
import { Confirm } from "../../utils/confirm";
import { formatCompactNumberKR } from "../../utils/numberFormat";
import { useThemeColors, getThemeColors } from "../../utils/themeColors";
import FilterPill from "../common/filter_pill";
import ChartCrosshairCursor from "../common/chart_crosshair_cursor";
import { getChartMemos, createChartMemo, updateChartMemo, deleteChartMemo } from "../../services/chartMemoApi";

const getMemoPalette = () => {
    const t = getThemeColors();
    return [t.brand, t.loss, t.gain, t.warning, t.brandLight, t.brandAccent].filter(Boolean);
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "";
    return Number(value).toLocaleString();
};

// 미국주식 일별 스냅샷 → { time: 해당일 자정 epoch ms, value: 평가액(KRW) } 오름차순 배열
const buildStockValueByDate = (points) => {
    return (points || [])
        .filter((p) => p && p.date)
        .map((p) => ({ time: dayjs(p.date).startOf("day").valueOf(), value: Number(p.value_krw) || 0 }))
        .sort((a, b) => a.time - b.time);
};

// 코인 잔고 시계열에 BTC 적립 평가액을 forward-fill로 병합 (DAS-181).
//
// BTC 는 이미 totalBalance 안에 섞여 있으므로 **더하는 게 아니라 갈라내는** 것이다:
//   coinExBtc(델타) + btcDca = 종전 코인 밴드
// 스택 총합(위쪽 윤곽선)은 변하지 않고 그 안이 나뉜다. 스냅샷 이전 구간은 btcDca=0 이라
// 가운데 밴드의 높이가 0 이 되고, 코인은 자연히 한 덩어리로 남는다 — 경계를 특별히 다루지 않아도 된다.
const mergeBtcSeries = (baseData, btcValueByDate) => {
    let idx = 0;
    let current = 0;
    return baseData.map((point) => {
        while (idx < btcValueByDate.length && btcValueByDate[idx].time <= point.time) {
            current = btcValueByDate[idx].value;
            idx++;
        }
        const btcDca = Math.min(current, point.totalBalance);
        return { ...point, btcDca, coinExBtc: Math.max(0, point.totalBalance - btcDca) };
    });
};

// 코인 잔고 시계열(baseData, 오름차순)에 주식 평가액을 forward-fill로 병합.
// 주말·휴장일 등 스냅샷이 없는 구간은 직전 값을 유지하고, 첫 스냅샷 이전 구간은 0.
const mergeUsStockSeries = (baseData, stockValueByDate) => {
    let idx = 0;
    let current = 0;
    return baseData.map((point) => {
        while (idx < stockValueByDate.length && stockValueByDate[idx].time <= point.time) {
            current = stockValueByDate[idx].value;
            idx++;
        }
        return { ...point, usStock: current, combined: point.totalBalance + current };
    });
};

class TotalBalanceChart extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            rangeKey: "all",
            baseData: this.buildBaseData(props.balanceHistory),
            includeUsStock: localStorage.getItem("history_includeUsStock") !== "false",
            memos: [],
            selectedMemo: null,
            popupPosition: null,
            showAddModal: false,
            addMemoDate: "",
            addMemoContent: "",
            addMemoColor: getMemoPalette()[0],
            editMemoId: null,
            editMemoContent: "",
            editMemoColor: getMemoPalette()[0],
            containerWidth: 0,
            activeColorFilter: null,
            memoSearchText: "",
            hoveredMemoId: null,
        };
        this.chartWrapperRef = React.createRef();
        this.railContainerRef = React.createRef();
        this.listContainerRef = React.createRef();
        this.memoItemRefs = {};
        this.highlightTimers = {};
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadMemos();
        this.resizeObserver = new ResizeObserver((entries) => {
            const w = entries[0].contentRect.width;
            this.setState({ containerWidth: w });
        });
        if (this.chartWrapperRef.current) {
            this.resizeObserver.observe(this.chartWrapperRef.current);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.resizeObserver?.disconnect();
        Object.values(this.highlightTimers).forEach(clearTimeout);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.balanceHistory !== this.props.balanceHistory) {
            this.setState({ baseData: this.buildBaseData(this.props.balanceHistory) });
        }
    }

    loadMemos = () => {
        getChartMemos()
            .then((res) => {
                if (!this._isMounted) return;
                const memos = res.data || [];
                Object.values(this.highlightTimers).forEach(clearTimeout);
                this.highlightTimers = {};
                this.memoItemRefs = {};
                this.setState({ memos });
            })
            .catch((err) => {
                console.error("[ChartMemo] loadMemos 실패:", err?.response?.status, err?.response?.data);
                if (!this._isMounted) return;
                showToast({ type: "error", message: "메모 로드에 실패했습니다." });
            });
    };

    buildBaseData = (balanceHistory) => {
        const LISTING_SNIPE = "ListingSnipe";
        const totalBalanceMap = {};
        (balanceHistory || []).forEach((exchangeArr) => {
            // per-pair dedup: 동일 hourKey에서 created가 가장 큰(최신) 레코드만 유지
            const pairMap = {};
            exchangeArr.forEach((item) => {
                // ListingSnipe는 DeltaFx와 같은 키 → 중복 합산 방지
                if (item.strategy_name === LISTING_SNIPE) return;
                const hourKey = dayjs(item.created).startOf("hour").valueOf();
                if (!pairMap[hourKey] || new Date(item.created) > new Date(pairMap[hourKey].created)) {
                    pairMap[hourKey] = item;
                }
            });
            Object.entries(pairMap).forEach(([hourKey, item]) => {
                totalBalanceMap[hourKey] = (totalBalanceMap[hourKey] || 0) + Number(item.total_balance);
            });
        });
        return Object.keys(totalBalanceMap)
            .map(Number)
            .sort((a, b) => a - b)
            .map((key) => ({ time: key, totalBalance: totalBalanceMap[key] }));
    };

    setRange = (rangeKey) => { this.setState({ rangeKey }); };

    toggleIncludeUsStock = () => {
        this.setState((prev) => {
            const next = !prev.includeUsStock;
            localStorage.setItem("history_includeUsStock", String(next));
            return { includeUsStock: next };
        });
    };

    // 코인/미국주식/합산 3줄 툴팁 (미국주식 포함 토글 ON일 때만 사용)
    renderStockTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        const coin = payload.find((p) => p.dataKey === "totalBalance")?.value ?? 0;
        const stock = payload.find((p) => p.dataKey === "usStock")?.value ?? 0;
        return (
            <div className="rounded-xl border border-surface-border bg-surface-elevated p-2 text-xs">
                <div className="text-content-secondary">{dayjs(label).format("YYYY-MM-DD HH:mm")}</div>
                <div className="mt-1 text-content">코인: ₩{formatCurrency(coin)}</div>
                <div className="text-content">미국주식: ₩{formatCurrency(stock)}</div>
                <div className="font-semibold text-content">합산: ₩{formatCurrency(coin + stock)}</div>
            </div>
        );
    };

    setTemporaryHighlight = (memoId) => {
        clearTimeout(this.highlightTimers[memoId]);
        this.toggleMemoHighlight(memoId, true);
        this.highlightTimers[memoId] = setTimeout(
            () => this.toggleMemoHighlight(memoId, false),
            2000,
        );
    };

    handleMemoMarkerClick = (memo) => {
        const el = this.memoItemRefs[memo.id];
        if (!el) return;
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        this.setTemporaryHighlight(memo.id);
    };

    handleMemoItemTap = (memo) => {
        this.setTemporaryHighlight(memo.id);
    };

    handleEditMemo = (memo) => {
        this.setState({
            selectedMemo: memo,
            editMemoContent: memo.content,
            editMemoColor: memo.color,
            editMemoId: memo.id,
            popupPosition: null,
        });
    };

    handleClosePopup = () => {
        this.setState({ selectedMemo: null, editMemoId: null });
    };

    handleUpdateMemo = () => {
        const { editMemoId, editMemoContent, editMemoColor } = this.state;
        updateChartMemo(editMemoId, editMemoContent, editMemoColor)
            .then(() => {
                if (!this._isMounted) return;
                this.setState({ selectedMemo: null, editMemoId: null });
                this.loadMemos();
            })
            .catch((err) => {
                console.error("[ChartMemo] 수정 실패:", err?.response?.status, err?.response?.data);
                if (!this._isMounted) return;
                const msg = err?.response?.data?.message || "메모 수정에 실패했습니다.";
                showToast({ type: "error", message: msg });
            });
    };

    handleDeleteMemo = async (memoId) => {
        const ok = await Confirm.show({ title: "메모 삭제", message: "이 메모를 삭제하시겠습니까?", variant: "danger" });
        if (!ok) return;
        if (!this._isMounted) return;
        deleteChartMemo(memoId)
            .then(() => {
                if (!this._isMounted) return;
                this.setState({ selectedMemo: null, editMemoId: null });
                this.loadMemos();
            })
            .catch((err) => {
                console.error("[ChartMemo] 삭제 실패:", err?.response?.status, err?.response?.data);
                if (!this._isMounted) return;
                const msg = err?.response?.data?.message || "메모 삭제에 실패했습니다.";
                showToast({ type: "error", message: msg });
            });
    };

    toggleMemoHighlight = (memoId, on) => {
        const selector = `[data-memo-id="${memoId}"]`;
        const roots = [this.railContainerRef.current, this.listContainerRef.current];
        roots.forEach((root) => {
            if (!root) return;
            root.querySelectorAll(selector).forEach((n) => n.classList.toggle("memo-hl", on));
        });
    };

    handleChartDoubleClick = (e) => {
        if (!e || !e.activeLabel) return;
        // activeLabel은 X축 time 값 (epoch ms)
        const clickedTime = e.activeLabel;
        this.setState({
            showAddModal: true,
            addMemoDate: dayjs(clickedTime).format("YYYY-MM-DDTHH:mm"),
            addMemoContent: "",
            addMemoColor: getMemoPalette()[0],
        });
    };

    handleOpenAddModal = () => {
        this.setState({
            showAddModal: true,
            addMemoDate: dayjs().format("YYYY-MM-DDTHH:mm"),
            addMemoContent: "",
            addMemoColor: getMemoPalette()[0],
        });
    };

    handleCloseAddModal = () => {
        this.setState({ showAddModal: false });
    };

    handleAddMemo = () => {
        const { addMemoDate, addMemoContent, addMemoColor } = this.state;
        if (!addMemoDate || !addMemoContent.trim()) {
            showToast({ type: "error", message: "날짜와 내용을 입력해주세요." });
            return;
        }
        this.setState({ addMemoSaving: true });
        createChartMemo(new Date(addMemoDate).toISOString(), addMemoContent.trim(), addMemoColor)
            .then(() => {
                if (!this._isMounted) return;
                this.setState({ showAddModal: false, addMemoSaving: false });
                this.loadMemos();
            })
            .catch((err) => {
                console.error("[ChartMemo] 추가 실패:", err?.response?.status, err?.response?.data);
                if (!this._isMounted) return;
                this.setState({ addMemoSaving: false });
                const msg = err?.response?.data?.message || err?.response?.data?.detail || "메모 저장에 실패했습니다.";
                showToast({ type: "error", message: msg });
            });
    };

    render() {
        const isMobile = isMobileViewport();
        const C = this.props.themeColors || getThemeColors();
        const MEMO_COLORS = getMemoPalette();
        const {
            rangeKey, baseData, memos, includeUsStock,
            selectedMemo, editMemoContent, editMemoColor,
            showAddModal, addMemoDate, addMemoContent, addMemoColor,
        } = this.state;
        const rangeOptions = [
            { key: "1d", label: "1일", hours: 24 },
            { key: "7d", label: "7일", hours: 24 * 7 },
            { key: "14d", label: "14일", hours: 24 * 14 },
            { key: "20d", label: "20일", hours: 24 * 20 },
            { key: "30d", label: "30일", hours: 24 * 30 },
            { key: "all", label: "전체", hours: null },
        ];
        const selectedRange = rangeOptions.find((o) => o.key === rangeKey) || rangeOptions[rangeOptions.length - 1];
        // 미국주식 시리즈 병합 — stockSeries가 없으면 기존과 동일한 baseData 참조
        const hasStockData = Array.isArray(this.props.stockSeries) && this.props.stockSeries.length > 0;
        const mergedBaseData = hasStockData
            ? mergeUsStockSeries(baseData, buildStockValueByDate(this.props.stockSeries))
            : baseData;
        const showStock = hasStockData && includeUsStock;

        // BTC 적립 밴드 (DAS-181) — 스냅샷이 있는 구간만 코인이 델타/BTC 로 갈라진다.
        const btcPoints = buildStockValueByDate(this.props.btcSeries);
        const hasBtcData = btcPoints.length > 0;
        const withBtc = hasBtcData ? mergeBtcSeries(mergedBaseData, btcPoints) : mergedBaseData;
        const showBtc = hasBtcData;
        const btcBoundaryTime = hasBtcData ? btcPoints[0].time : null;
        const displayData = selectedRange.hours ? withBtc.slice(-selectedRange.hours) : withBtc;
        const height = isMobile ? 280 : 480;
        const leftAxisWidth = isMobile ? 56 : 90;
        const chartMargin = { top: 10, right: isMobile ? 12 : 30, left: isMobile ? 8 : leftAxisWidth / 2, bottom: 0 };
        const domainValues = displayData
            .map((item) => (showStock ? item.combined : item.totalBalance))
            .filter((v) => Number.isFinite(v));
        const domain = getPaddedDomain(domainValues);
        const formatAxisValue = (value) => {
            const formatted = isMobile ? formatCompactNumberKR(value) : formatCurrency(value);
            return formatted ? `₩${formatted}` : "";
        };
        const titleSuffix = selectedRange.hours ? `최근 ${selectedRange.label}` : "전체";

        // Position math for marker rail (chartMargin/leftAxisWidth 변경 시 함께 업데이트할 것)
        const plotLeftPx = chartMargin.left + leftAxisWidth;
        const plotRightPx = chartMargin.right;
        const { containerWidth } = this.state;
        const innerWidth = Math.max(0, containerWidth - plotLeftPx - plotRightPx);

        // 현재 표시 범위 내 메모만 필터링
        const displayTimeMin = displayData.length ? displayData[0].time : 0;
        const displayTimeMax = displayData.length ? displayData[displayData.length - 1].time : 0;
        const rangeSpan = displayTimeMax - displayTimeMin;
        const { activeColorFilter, memoSearchText, hoveredMemoId } = this.state;
        const searchLc = memoSearchText.trim().toLowerCase();
        const matchesSearch = (m) => {
            if (!searchLc) return true;
            return (m.content || "").toLowerCase().includes(searchLc);
        };
        // 범위 + 검색 통과 (색상 필터 제외) — 카운트 계산용
        const inRangeAndSearch = memos.filter((m) => {
            const t = new Date(m.memo_date).getTime();
            if (t < displayTimeMin || t > displayTimeMax) return false;
            return matchesSearch(m);
        });
        const colorCounts = inRangeAndSearch.reduce((acc, m) => {
            acc[m.color] = (acc[m.color] || 0) + 1;
            return acc;
        }, {});
        // 최종 visible = 범위 + 검색 + 색상 필터
        const visibleMemos = inRangeAndSearch.filter(
            (m) => !activeColorFilter || m.color === activeColorFilter
        );
        const uniqueColors = [...new Set(inRangeAndSearch.map((m) => m.color))];
        // hover popover 대상
        const hoveredMemo = hoveredMemoId
            ? visibleMemos.find((m) => m.id === hoveredMemoId)
            : null;
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-elevated sm:p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-content-secondary">
                            종합 잔고 차트 ({titleSuffix})
                        </div>
                        {(showStock || showBtc) && (
                            <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
                                <span className="inline-flex items-center gap-1">
                                    <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: C.brand }} aria-hidden="true" />
                                    {showBtc ? "델타전략" : "코인"}
                                </span>
                                {showBtc && (
                                    <span className="inline-flex items-center gap-1">
                                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: C.warning }} aria-hidden="true" />
                                        BTC 적립
                                    </span>
                                )}
                                {showStock && (
                                    <span className="inline-flex items-center gap-1">
                                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: C.contentSecondary }} aria-hidden="true" />
                                        미국주식
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    {showBtc && (
                        <span className="text-xs text-content-muted">
                            BTC 적립은 이 날짜부터 분리해 보여줍니다 — 그 이전은 잔고 기록에 코인별 내역이 없어 코인 한 덩어리입니다
                        </span>
                    )}
                    <span className="text-xs text-content-muted" title="차트를 더블클릭하여 메모 추가">
                        차트 더블클릭으로 메모 추가
                    </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sm:mt-3">
                    <span className="text-content-muted">기간</span>
                    {rangeOptions.map((option) => (
                        <FilterPill
                            key={option.key}
                            active={option.key === selectedRange.key}
                            onClick={() => this.setRange(option.key)}
                            variant="solid"
                        >
                            {option.label}
                        </FilterPill>
                    ))}
                    {hasStockData && (
                        <button
                            type="button"
                            onClick={this.toggleIncludeUsStock}
                            aria-label="미국주식 포함"
                            aria-pressed={includeUsStock}
                            className={`ml-auto min-h-[44px] rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                includeUsStock
                                    ? "border-brand bg-brand-subtle text-brand-light"
                                    : "border-surface-border text-content-secondary hover:bg-surface-hover"
                            }`}
                        >
                            미국주식 포함
                        </button>
                    )}
                </div>
                {displayData.length > 0 && visibleMemos.length > 0 && (() => {
                    const hoveredRatio = hoveredMemo
                        ? (() => {
                            const t = new Date(hoveredMemo.memo_date).getTime();
                            return rangeSpan > 0 ? (t - displayTimeMin) / rangeSpan : 0.5;
                        })()
                        : null;
                    const hoverLeftPx = hoveredRatio != null ? hoveredRatio * innerWidth : null;
                    // 좌우 끝 근접 시 popover가 화면 밖으로 나가지 않도록 clamp
                    const popoverMaxW = 240;
                    const popoverOffset =
                        hoverLeftPx == null
                            ? 0
                            : Math.max(
                                  popoverMaxW / 2,
                                  Math.min(innerWidth - popoverMaxW / 2, hoverLeftPx)
                              );
                    return (
                    <div
                        ref={this.railContainerRef}
                        className="relative mt-2 h-6 w-full"
                        style={{ paddingLeft: plotLeftPx, paddingRight: plotRightPx }}
                        aria-label="메모 마커"
                    >
                        <div className="relative h-full w-full">
                            {visibleMemos.map((memo) => {
                                const t = new Date(memo.memo_date).getTime();
                                const ratio = rangeSpan > 0 ? (t - displayTimeMin) / rangeSpan : 0.5;
                                if (ratio < 0 || ratio > 1) return null;
                                const left = ratio * innerWidth;
                                return (
                                    <button
                                        key={memo.id}
                                        data-memo-id={memo.id}
                                        onClick={() => this.handleMemoMarkerClick(memo)}
                                        onMouseEnter={() => {
                                            this.toggleMemoHighlight(memo.id, true);
                                            this.setState({ hoveredMemoId: memo.id });
                                        }}
                                        onMouseLeave={() => {
                                            this.toggleMemoHighlight(memo.id, false);
                                            this.setState((prev) =>
                                                prev.hoveredMemoId === memo.id
                                                    ? { hoveredMemoId: null }
                                                    : null
                                            );
                                        }}
                                        onFocus={() => {
                                            this.toggleMemoHighlight(memo.id, true);
                                            this.setState({ hoveredMemoId: memo.id });
                                        }}
                                        onBlur={() => {
                                            this.toggleMemoHighlight(memo.id, false);
                                            this.setState((prev) =>
                                                prev.hoveredMemoId === memo.id
                                                    ? { hoveredMemoId: null }
                                                    : null
                                            );
                                        }}
                                        aria-label={`메모: ${memo.content}`}
                                        className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface-card transition-transform hover:scale-125 [&.memo-hl]:scale-125 [&.memo-hl]:ring-content-inverse/90"
                                        style={{ left: `${left}px`, backgroundColor: memo.color }}
                                    />
                                );
                            })}
                            {hoveredMemo && hoverLeftPx != null && (
                                <div
                                    className="pointer-events-none absolute bottom-full z-20 mb-2 w-60 -translate-x-1/2 rounded-lg border border-surface-border bg-surface-elevated p-2 shadow-elevated"
                                    style={{ left: `${popoverOffset}px`, maxWidth: `${popoverMaxW}px` }}
                                    role="tooltip"
                                >
                                    <div className="flex items-center gap-1.5">
                                        <span
                                            className="inline-block h-2 w-2 shrink-0 rounded-full"
                                            style={{ backgroundColor: hoveredMemo.color }}
                                            aria-hidden="true"
                                        />
                                        <span className="font-num text-xs text-content-muted">
                                            {dayjs(hoveredMemo.memo_date).format("YYYY-MM-DD HH:mm")}
                                        </span>
                                    </div>
                                    <div className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-content">
                                        {hoveredMemo.content}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    );
                })()}
                <div ref={this.chartWrapperRef} className="mt-3 sm:mt-4" style={{ height }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={displayData} margin={chartMargin} onDoubleClick={this.handleChartDoubleClick}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.surfaceElevated} />
                            <XAxis dataKey="time" type="number" tickFormatter={(v) => dayjs(v).format("MM-DD HH:mm")} tick={{ fontSize: 12, fill: C.contentSecondary }} domain={["dataMin", "dataMax"]} />
                            <YAxis width={leftAxisWidth} tickMargin={6} tick={{ fontSize: 12, fill: C.contentSecondary }} tickFormatter={formatAxisValue} domain={domain} />
                            {showStock ? (
                                <Tooltip
                                    cursor={
                                        <ChartCrosshairCursor
                                            stroke={C.contentMuted}
                                            getDomain={() => domain}
                                        />
                                    }
                                    content={this.renderStockTooltip}
                                />
                            ) : (
                                <Tooltip
                                    cursor={
                                        <ChartCrosshairCursor
                                            stroke={C.contentMuted}
                                            getDomain={() => domain}
                                        />
                                    }
                                    formatter={(value) => [`₩${formatCurrency(value)}`, "총 잔고"]}
                                    labelFormatter={(v) => dayjs(v).format("YYYY-MM-DD HH:mm")}
                                    contentStyle={{ backgroundColor: C.surfaceElevated, border: `1px solid ${C.surfaceBorder}`, borderRadius: "0.75rem" }}
                                    labelStyle={{ color: C.contentSecondary }}
                                    itemStyle={{ color: C.content }}
                                />
                            )}
                            {btcBoundaryTime && (
                                <ReferenceLine
                                    x={btcBoundaryTime}
                                    stroke={C.warning}
                                    strokeDasharray="4 3"
                                    label={{ value: "BTC 분리 시작", position: "insideTopRight", fill: C.warning, fontSize: 11 }}
                                />
                            )}
                            {/* BTC 스냅샷이 있으면 코인 밴드를 델타/BTC 로 가른다 — 스택 총합은 그대로다 (DAS-181) */}
                            <Area
                                type="monotone"
                                dataKey={showBtc ? "coinExBtc" : "totalBalance"}
                                stackId={showStock || showBtc ? "total" : undefined}
                                stroke={C.brand}
                                fill="url(#colorBalanceDark)"
                                strokeWidth={2}
                            />
                            {showBtc && (
                                <Area type="monotone" dataKey="btcDca" stackId="total" stroke={C.warning} fill="url(#colorBtcDark)" strokeWidth={1.5} />
                            )}
                            {showStock && (
                                <Area type="monotone" dataKey="usStock" stackId="total" stroke={C.contentSecondary} fill="url(#colorStockDark)" strokeWidth={1.5} />
                            )}
                            <defs>
                                <linearGradient id="colorBalanceDark" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={C.brand} stopOpacity={0.3} />
                                    <stop offset="100%" stopColor={C.brand} stopOpacity={0.02} />
                                </linearGradient>
                                {showBtc && (
                                    <linearGradient id="colorBtcDark" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={C.warning} stopOpacity={0.34} />
                                        <stop offset="100%" stopColor={C.warning} stopOpacity={0.04} />
                                    </linearGradient>
                                )}
                                {showStock && (
                                    <linearGradient id="colorStockDark" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={C.contentSecondary} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={C.contentSecondary} stopOpacity={0.02} />
                                    </linearGradient>
                                )}
                            </defs>
                            {visibleMemos.map((memo) => (
                                <ReferenceLine
                                    key={memo.id}
                                    x={new Date(memo.memo_date).getTime()}
                                    stroke={memo.color}
                                    strokeWidth={1}
                                    strokeOpacity={0.3}
                                    strokeDasharray="4 2"
                                />
                            ))}
                            {!isMobile && <Brush dataKey="time" height={24} stroke={C.brand} tickFormatter={(v) => dayjs(v).format("MM-DD HH:mm")} />}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* 메모 리스트 패널 */}
                {(inRangeAndSearch.length > 0 || memoSearchText || activeColorFilter) && (
                    <div className="mt-3 rounded-xl border border-surface-border bg-surface-hover/40 p-3 lg:sticky lg:top-4">
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="text-xs font-semibold text-content-secondary">
                                    메모 ({visibleMemos.length}개)
                                </div>
                                {uniqueColors.length > 1 && (
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => this.setState({ activeColorFilter: null })}
                                            className={`rounded px-1.5 py-0.5 text-xs transition-colors ${!activeColorFilter ? "bg-brand/20 text-brand-light border border-brand/30" : "text-content-muted hover:text-content-secondary"}`}
                                        >
                                            전체
                                        </button>
                                        {uniqueColors.map((color) => {
                                            const count = colorCounts[color] || 0;
                                            const isActive = activeColorFilter === color;
                                            return (
                                                <button
                                                    key={color}
                                                    onClick={() => this.setState({ activeColorFilter: isActive ? null : color })}
                                                    className={`inline-flex items-center gap-1 rounded-full border-2 px-1.5 py-0.5 transition-transform ${isActive ? "scale-105 border-content-inverse" : "border-transparent hover:scale-105"}`}
                                                    aria-label={`${color} 색상 필터 (${count}개)`}
                                                    aria-pressed={isActive}
                                                >
                                                    <span
                                                        className="inline-block h-3 w-3 rounded-full"
                                                        style={{ backgroundColor: color }}
                                                        aria-hidden="true"
                                                    />
                                                    <span className="font-num text-xs text-content-muted">{count}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <svg
                                        aria-hidden="true"
                                        className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-muted"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="M21 21l-4.35-4.35" />
                                    </svg>
                                    <input
                                        type="text"
                                        value={memoSearchText}
                                        onChange={(e) => this.setState({ memoSearchText: e.target.value })}
                                        placeholder="메모 검색..."
                                        aria-label="메모 검색"
                                        className="w-36 rounded-lg border border-surface-border bg-surface-card pl-7 pr-6 py-1 text-xs text-content placeholder:text-content-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand sm:w-48"
                                    />
                                    {memoSearchText && (
                                        <button
                                            type="button"
                                            onClick={() => this.setState({ memoSearchText: "" })}
                                            aria-label="검색 지우기"
                                            className="absolute right-1 top-1/2 -translate-y-1/2 rounded px-1 text-xs text-content-muted hover:text-content"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                                <button
                                    onClick={this.handleOpenAddModal}
                                    className="min-h-[44px] rounded-lg border border-surface-border px-2 py-1 text-xs text-brand-light hover:text-brand"
                                >
                                    + 메모 추가
                                </button>
                            </div>
                        </div>
                        {visibleMemos.length === 0 && (
                            <div className="rounded-lg border border-surface-border bg-surface-card px-3 py-6 text-center text-xs text-content-muted">
                                {memoSearchText
                                    ? `"${memoSearchText}"와 일치하는 메모가 없습니다.`
                                    : "표시할 메모가 없습니다."}
                            </div>
                        )}
                        <div
                            ref={this.listContainerRef}
                            className="max-h-64 space-y-2 overflow-y-auto overscroll-contain pr-1 lg:max-h-[calc(100vh-20rem)]"
                            aria-label="메모 목록"
                        >
                            {[...visibleMemos].sort((a, b) => new Date(b.memo_date).getTime() - new Date(a.memo_date).getTime()).map((memo) => (
                                <div
                                    key={memo.id}
                                    data-memo-id={memo.id}
                                    ref={(el) => { if (el) this.memoItemRefs[memo.id] = el; }}
                                    onMouseEnter={() => this.toggleMemoHighlight(memo.id, true)}
                                    onMouseLeave={() => this.toggleMemoHighlight(memo.id, false)}
                                    onClick={() => this.handleMemoItemTap(memo)}
                                    className="flex min-h-[44px] items-start gap-2 rounded-lg border border-surface-border bg-surface-card p-2.5 transition-colors hover:border-brand/50 [&.memo-hl]:ring-2 [&.memo-hl]:ring-content-inverse/80"
                                >
                                    <span
                                        className="mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                                        style={{ backgroundColor: memo.color }}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs text-content-muted">
                                            {dayjs(memo.memo_date).format("YYYY-MM-DD HH:mm")}
                                        </div>
                                        <div className="mt-0.5 whitespace-pre-wrap break-words text-sm text-content-secondary">
                                            {memo.content}
                                        </div>
                                    </div>
                                    <div className="flex shrink-0 gap-1">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); this.handleEditMemo(memo); }}
                                            className="min-h-[44px] min-w-[44px] rounded px-2 text-xs text-brand-light hover:text-brand"
                                            aria-label="메모 수정"
                                        >
                                            수정
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); this.handleDeleteMemo(memo.id); }}
                                            className="min-h-[44px] min-w-[44px] rounded px-2 text-xs text-loss hover:text-loss-light"
                                            aria-label="메모 삭제"
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 메모 상세 팝업 */}
                {selectedMemo && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-overlay"
                        onClick={this.handleClosePopup}
                        role="button"
                        tabIndex={0}
                        aria-label="닫기"
                        onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Enter") this.handleClosePopup(); }}
                    >
                        <div
                            className="w-80 rounded-2xl border border-surface-border bg-surface-card p-4 shadow-elevated"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs text-content-muted">
                                    {dayjs(selectedMemo.memo_date).format("YYYY-MM-DD HH:mm")}
                                </span>
                                <button
                                    onClick={() => this.handleDeleteMemo(selectedMemo.id)}
                                    className="text-xs text-loss hover:text-loss-light"
                                >
                                    삭제
                                </button>
                            </div>
                            <textarea
                                className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                                rows={3}
                                value={editMemoContent}
                                onChange={(e) => this.setState({ editMemoContent: e.target.value })}
                                aria-label="메모 내용"
                            />
                            <div className="mt-2 flex items-center gap-1.5">
                                <span className="text-xs text-content-muted">색상</span>
                                {MEMO_COLORS.map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => this.setState({ editMemoColor: c })}
                                        className={`h-5 w-5 rounded-full border-2 transition-transform ${editMemoColor === c ? "scale-125 border-content-inverse" : "border-transparent hover:scale-110"}`}
                                        style={{ backgroundColor: c }}
                                        aria-label={`색상 선택: ${c}`}
                                    />
                                ))}
                            </div>
                            <div className="mt-3 flex justify-end gap-2">
                                <button
                                    onClick={this.handleClosePopup}
                                    className="rounded-lg border border-surface-border px-3 py-1.5 text-xs text-content-secondary hover:text-content"
                                >
                                    취소
                                </button>
                                <button
                                    onClick={this.handleUpdateMemo}
                                    className="rounded-lg bg-brand/20 border border-brand/30 px-3 py-1.5 text-xs text-brand-light hover:bg-brand/30"
                                >
                                    저장
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 메모 추가 모달 */}
                {showAddModal && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-overlay"
                        onClick={this.handleCloseAddModal}
                        role="button"
                        tabIndex={0}
                        aria-label="닫기"
                        onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Enter") this.handleCloseAddModal(); }}
                    >
                        <div
                            className="w-80 rounded-2xl border border-surface-border bg-surface-card p-4 shadow-elevated"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mb-3 text-sm font-semibold text-content-secondary">메모 추가</div>
                            <div className="mb-2">
                                <label className="mb-1 block text-xs text-content-muted">날짜/시간</label>
                                <input
                                    type="datetime-local"
                                    className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                                    value={addMemoDate}
                                    onChange={(e) => this.setState({ addMemoDate: e.target.value })}
                                />
                            </div>
                            <div className="mb-2">
                                <label className="mb-1 block text-xs text-content-muted">내용</label>
                                <textarea
                                    className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                                    rows={3}
                                    placeholder="예: 서버 재시작, 잔고 이체, 스파이크 원인 등"
                                    value={addMemoContent}
                                    onChange={(e) => this.setState({ addMemoContent: e.target.value })}
                                    aria-label="메모 내용"
                                    autoFocus
                                />
                            </div>
                            <div className="mb-3 flex items-center gap-1.5">
                                <span className="text-xs text-content-muted">색상</span>
                                {MEMO_COLORS.map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => this.setState({ addMemoColor: c })}
                                        className={`h-5 w-5 rounded-full border-2 transition-transform ${addMemoColor === c ? "scale-125 border-content-inverse" : "border-transparent hover:scale-110"}`}
                                        style={{ backgroundColor: c }}
                                        aria-label={`색상 선택: ${c}`}
                                    />
                                ))}
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={this.handleCloseAddModal}
                                    className="rounded-lg border border-surface-border px-3 py-1.5 text-xs text-content-secondary hover:text-content"
                                >
                                    취소
                                </button>
                                <button
                                    onClick={this.handleAddMemo}
                                    disabled={this.state.addMemoSaving}
                                    className="rounded-lg bg-brand/20 border border-brand/30 px-3 py-1.5 text-xs text-brand-light hover:bg-brand/30 disabled:opacity-50"
                                >
                                    {this.state.addMemoSaving ? "저장 중..." : "추가"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

const TotalBalanceChartWithTheme = (props) => {
    const themeColors = useThemeColors();
    return <TotalBalanceChart {...props} themeColors={themeColors} />;
};
TotalBalanceChartWithTheme.displayName = "TotalBalanceChartWithTheme";
export default TotalBalanceChartWithTheme;
